import React, { CSSProperties, useMemo } from 'react';
import ReactModal, { Styles } from 'react-modal';

import { NavState, NavItem, MenuColumnTheme } from '../interfaces';
import MenuColumn from './MenuColumn';
import * as Styled from './styled';

export type ComponentProps = {
  /**
   * Collection of items to show in DropDownMenu in first-level ordered. If is an empty array, hides menu.
   */
  firstColNav: NavState;
  /**
   * Collection of items to show in DropDownMenu in second-level ordered. If is an empty array, hides menu.
   */
  secondColNav: NavState;
  /**
   * Collection of items to show in DropDownMenu in third-level ordered. If is an empty array, hides menu.
   */
  thirdColNav: NavState;
  /**
   * Handler when the first sub-nav column is selected
   * @param item selected 1st level sub menu item
   */
  onFirstLevelColSelect: (item: NavItem) => void;
  /**
   *  Handler when the second sub-nav column is selected
   * @param item selected 2nd level sub menu item
   */
  onSecondLevelColSelect: (item: NavItem) => void;
  /**
   *  Callback handler when the menu is closed.
   */
  onClose: () => void;
  /**
   * Optional the px height of the header. Offsets the overlay of the dropdown nav menu. Defaults to 80.
   */
  headerHeight?: number;
  /**
   * Identifies the root of the app, which can be set to aria-hidden when the nav is open.
   */
  appElement?: HTMLElement | null;
};

const SubMenu: React.FC<ComponentProps> = ({
  firstColNav,
  secondColNav,
  thirdColNav,
  onFirstLevelColSelect,
  onSecondLevelColSelect,
  onClose,
  headerHeight = 100,
  appElement
}) => {
  const customStyles = useMemo(() => {
    const overlayStyles: CSSProperties = {
      backgroundColor: 'rgba(142, 144, 147, 0.6)',
      top: `${headerHeight}px`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      zIndex: 100
    };
    const contentStyles: CSSProperties = {
      position: 'static',
      width: 'calc(100% - 16px)',
      maxWidth: '1482px',
      margin: '8px 8px 0 8px',
      borderRadius: '0',
      border: 'none',
      padding: '0'
    };
    return {
      overlay: { ...overlayStyles },
      content: { ...contentStyles }
    };
  }, [headerHeight]);
  // This sub menu renders different menu columns aligned from left to right to support 3 nested level columns (first, second and third which is the deepest nesting available)
  return (
    <ReactModal
      isOpen={!!firstColNav.id}
      style={customStyles as Styles}
      onRequestClose={onClose}
      appElement={appElement ?? document.body}>
      <Styled.MenuContent>
        <MenuColumn
          activeId={secondColNav.id}
          items={firstColNav.items}
          onSelect={onFirstLevelColSelect}
          colorScheme={MenuColumnTheme.DARK_GREY}
        />
        <MenuColumn
          activeId={thirdColNav.id}
          items={secondColNav.items}
          onSelect={onSecondLevelColSelect}
          colorScheme={MenuColumnTheme.BLUE_GREY}
        />
        <MenuColumn items={thirdColNav.items} colorScheme={MenuColumnTheme.BLUE_GREY_75} />
      </Styled.MenuContent>
    </ReactModal>
  );
};

export default SubMenu;
